/**
 * Enhanced RBAC (Role-Based Access Control) Utilities
 * Phase 3: Field-level permissions and dynamic UI control
 */

// Role definitions and permissions
export const ROLES = {
  OWNER: 'owner',
  ADMIN: 'admin', 
  FINANCE: 'finance',
  OPS: 'ops',
  WAREHOUSE: 'warehouse',
  SALES: 'sales',
  PRICING: 'pricing',  // CRITICAL FIX: Add Pricing role for pricing-related columns
  ANALYST: 'analyst',
  VIEWER: 'viewer'
};

// Field categories for permission mapping
export const FIELD_CATEGORIES = {
  GENERAL: 'general',
  PURCHASING: 'purchasing', 
  PLANNING: 'planning',
  SALES: 'sales',
  STORAGE: 'storage',
  SHELF_LIFE: 'shelf_life',
  PACKAGING: 'packaging',
  PRICING: 'pricing',
  COSTING: 'costing'
};

// Permission matrix: role -> field categories -> access level
export const PERMISSION_MATRIX = {
  [ROLES.OWNER]: {
    [FIELD_CATEGORIES.GENERAL]: 'write',
    [FIELD_CATEGORIES.PURCHASING]: 'write',
    [FIELD_CATEGORIES.PLANNING]: 'write', 
    [FIELD_CATEGORIES.SALES]: 'write',
    [FIELD_CATEGORIES.STORAGE]: 'write',
    [FIELD_CATEGORIES.SHELF_LIFE]: 'write',
    [FIELD_CATEGORIES.PACKAGING]: 'write',
    [FIELD_CATEGORIES.PRICING]: 'write',
    [FIELD_CATEGORIES.COSTING]: 'write'
  },
  
  [ROLES.ADMIN]: {
    [FIELD_CATEGORIES.GENERAL]: 'write',
    [FIELD_CATEGORIES.PURCHASING]: 'write',
    [FIELD_CATEGORIES.PLANNING]: 'write',
    [FIELD_CATEGORIES.SALES]: 'write', 
    [FIELD_CATEGORIES.STORAGE]: 'write',
    [FIELD_CATEGORIES.SHELF_LIFE]: 'write',
    [FIELD_CATEGORIES.PACKAGING]: 'write',
    [FIELD_CATEGORIES.PRICING]: 'write',
    [FIELD_CATEGORIES.COSTING]: 'write'
  },
  
  [ROLES.FINANCE]: {
    [FIELD_CATEGORIES.GENERAL]: 'read',
    [FIELD_CATEGORIES.PURCHASING]: 'read',
    [FIELD_CATEGORIES.PLANNING]: 'read',
    [FIELD_CATEGORIES.SALES]: 'read',
    [FIELD_CATEGORIES.STORAGE]: 'read', 
    [FIELD_CATEGORIES.SHELF_LIFE]: 'read',
    [FIELD_CATEGORIES.PACKAGING]: 'read',
    [FIELD_CATEGORIES.PRICING]: 'write', // Finance can edit pricing
    [FIELD_CATEGORIES.COSTING]: 'write'  // Finance can edit costing
  },
  
  [ROLES.OPS]: {
    [FIELD_CATEGORIES.GENERAL]: 'read',
    [FIELD_CATEGORIES.PURCHASING]: 'write', // Ops can edit purchasing
    [FIELD_CATEGORIES.PLANNING]: 'write',   // Ops can edit planning
    [FIELD_CATEGORIES.SALES]: 'read',
    [FIELD_CATEGORIES.STORAGE]: 'write',    // Ops can edit storage
    [FIELD_CATEGORIES.SHELF_LIFE]: 'write', // Ops can edit shelf life
    [FIELD_CATEGORIES.PACKAGING]: 'write',  // Ops can edit packaging
    [FIELD_CATEGORIES.PRICING]: 'read',
    [FIELD_CATEGORIES.COSTING]: 'read'
  },
  
  [ROLES.WAREHOUSE]: {
    [FIELD_CATEGORIES.GENERAL]: 'read',
    [FIELD_CATEGORIES.PURCHASING]: 'read',
    [FIELD_CATEGORIES.PLANNING]: 'write',   // Warehouse can edit planning
    [FIELD_CATEGORIES.SALES]: 'read',
    [FIELD_CATEGORIES.STORAGE]: 'write',    // Warehouse can edit storage
    [FIELD_CATEGORIES.SHELF_LIFE]: 'write', // Warehouse can edit shelf life
    [FIELD_CATEGORIES.PACKAGING]: 'read',
    [FIELD_CATEGORIES.PRICING]: 'read',
    [FIELD_CATEGORIES.COSTING]: 'read'
  },
  
  [ROLES.SALES]: {
    [FIELD_CATEGORIES.GENERAL]: 'read',
    [FIELD_CATEGORIES.PURCHASING]: 'read',
    [FIELD_CATEGORIES.PLANNING]: 'read',
    [FIELD_CATEGORIES.SALES]: 'write',      // Sales can edit sales
    [FIELD_CATEGORIES.STORAGE]: 'read',
    [FIELD_CATEGORIES.SHELF_LIFE]: 'read',
    [FIELD_CATEGORIES.PACKAGING]: 'read',
    [FIELD_CATEGORIES.PRICING]: 'write',    // Sales can edit pricing
    [FIELD_CATEGORIES.COSTING]: 'read'
  },
  
  [ROLES.PRICING]: {
    [FIELD_CATEGORIES.GENERAL]: 'read',
    [FIELD_CATEGORIES.PURCHASING]: 'read',
    [FIELD_CATEGORIES.PLANNING]: 'read',
    [FIELD_CATEGORIES.SALES]: 'read',
    [FIELD_CATEGORIES.STORAGE]: 'read',
    [FIELD_CATEGORIES.SHELF_LIFE]: 'read',
    [FIELD_CATEGORIES.PACKAGING]: 'read',
    [FIELD_CATEGORIES.PRICING]: 'write',    // Pricing role can edit pricing
    [FIELD_CATEGORIES.COSTING]: 'write'     // Pricing role can edit costing
  },
  
  [ROLES.ANALYST]: {
    [FIELD_CATEGORIES.GENERAL]: 'read',
    [FIELD_CATEGORIES.PURCHASING]: 'read',
    [FIELD_CATEGORIES.PLANNING]: 'read',
    [FIELD_CATEGORIES.SALES]: 'read',
    [FIELD_CATEGORIES.STORAGE]: 'read',
    [FIELD_CATEGORIES.SHELF_LIFE]: 'read',
    [FIELD_CATEGORIES.PACKAGING]: 'read',
    [FIELD_CATEGORIES.PRICING]: 'read',
    [FIELD_CATEGORIES.COSTING]: 'read'
  },
  
  [ROLES.VIEWER]: {
    [FIELD_CATEGORIES.GENERAL]: 'read',
    [FIELD_CATEGORIES.PURCHASING]: 'read',
    [FIELD_CATEGORIES.PLANNING]: 'read',
    [FIELD_CATEGORIES.SALES]: 'read',
    [FIELD_CATEGORIES.STORAGE]: 'read',
    [FIELD_CATEGORIES.SHELF_LIFE]: 'read',
    [FIELD_CATEGORIES.PACKAGING]: 'read',
    [FIELD_CATEGORIES.PRICING]: 'read',
    [FIELD_CATEGORIES.COSTING]: 'read'
  }
};

// Utility functions
export const getUserRoles = () => {
  // For demo purposes, return mock roles
  // In production, this would come from user context/JWT/API
  return [ROLES.FINANCE]; // Default to Finance role for testing
};

export const hasPermission = (userRoles, fieldCategory, requiredAccess = 'read') => {
  if (!userRoles || userRoles.length === 0) {
    return false;
  }
  
  // Check if any of the user's roles grants the required access
  return userRoles.some(role => {
    const rolePermissions = PERMISSION_MATRIX[role];
    if (!rolePermissions) return false;
    
    const fieldAccess = rolePermissions[fieldCategory];
    if (!fieldAccess) return false;
    
    // Write permission includes read permission
    if (requiredAccess === 'read') {
      return fieldAccess === 'read' || fieldAccess === 'write';
    }
    
    return fieldAccess === requiredAccess;
  });
};

export const canEditField = (userRoles, fieldCategory) => {
  return hasPermission(userRoles, fieldCategory, 'write');
};

export const canViewField = (userRoles, fieldCategory) => {
  return hasPermission(userRoles, fieldCategory, 'read');
};

// Map column IDs to field categories (from catalog.js)
export const getFieldCategory = (columnId) => {
  if (!columnId) return null;
  
  const categoryMap = {
    // General fields (match catalog.js IDs)
    'general.sku': FIELD_CATEGORIES.GENERAL,
    'general.name': FIELD_CATEGORIES.GENERAL,
    'general.category': FIELD_CATEGORIES.GENERAL,
    'general.subcategory': FIELD_CATEGORIES.GENERAL,
    'general.status': FIELD_CATEGORIES.GENERAL,
    'general.upc': FIELD_CATEGORIES.GENERAL,
    'general.brand': FIELD_CATEGORIES.GENERAL,
    'general.description': FIELD_CATEGORIES.GENERAL,
    
    // Purchasing fields (match catalog.js IDs)
    'purch.orderingUom': FIELD_CATEGORIES.PURCHASING,
    'purch.leadTimeDays': FIELD_CATEGORIES.PURCHASING,
    'purch.supplierPrice': FIELD_CATEGORIES.PURCHASING,
    'purch.supplierName': FIELD_CATEGORIES.PURCHASING,
    
    // Planning fields (match catalog.js IDs)
    'plan.reorderPoint': FIELD_CATEGORIES.PLANNING,
    'plan.targetStock': FIELD_CATEGORIES.PLANNING,
    'plan.onHand': FIELD_CATEGORIES.PLANNING,
    
    // Sales fields (match catalog.js IDs)
    'sales.sellingUom': FIELD_CATEGORIES.SALES,
    'sales.defaultPrice': FIELD_CATEGORIES.SALES,
    'sales.channel': FIELD_CATEGORIES.SALES,
    
    // Storage fields (match catalog.js IDs)
    'storage.location': FIELD_CATEGORIES.STORAGE,
    'storage.condition': FIELD_CATEGORIES.STORAGE,
    
    // Shelf life fields (match catalog.js IDs)
    'shelfLife.days': FIELD_CATEGORIES.SHELF_LIFE,
    'shelfLife.batchNo': FIELD_CATEGORIES.SHELF_LIFE,
    'shelfLife.expirySoon': FIELD_CATEGORIES.SHELF_LIFE,
    
    // Packaging fields (match catalog.js IDs)
    'pack.casePackQty': FIELD_CATEGORIES.PACKAGING,
    'pack.unitGrossWeight': FIELD_CATEGORIES.PACKAGING,
    'pack.unitNetWeight': FIELD_CATEGORIES.PACKAGING,
    'pack.unitDims': FIELD_CATEGORIES.PACKAGING,
    
    // Pricing fields (match catalog.js IDs) - Restricted to finance roles
    'price.cogm': FIELD_CATEGORIES.PRICING,
    'price.totalLandedCost': FIELD_CATEGORIES.PRICING,
    'price.effectiveCost': FIELD_CATEGORIES.PRICING,
    'price.cogs': FIELD_CATEGORIES.PRICING,
    'price.grossProfit': FIELD_CATEGORIES.PRICING,
    'price.grossMarginPct': FIELD_CATEGORIES.PRICING,
    'price.netMarginPct': FIELD_CATEGORIES.PRICING,
    'price.profitAtPrice': FIELD_CATEGORIES.PRICING
  };
  
  return categoryMap[columnId] || FIELD_CATEGORIES.GENERAL;
};

// Filter columns based on RBAC permissions
export const filterColumnsByRBAC = (columns, userRoles) => {
  if (!userRoles || userRoles.length === 0) {
    return [];
  }
  
  return columns.filter(column => {
    const fieldCategory = getFieldCategory(column.id);
    return canViewField(userRoles, fieldCategory);
  });
};

// Get access level for a field
export const getFieldAccessLevel = (userRoles, fieldCategory) => {
  if (!userRoles || userRoles.length === 0) {
    return 'none';
  }
  
  if (canEditField(userRoles, fieldCategory)) {
    return 'write';
  } else if (canViewField(userRoles, fieldCategory)) {
    return 'read';
  }
  
  return 'none';
};

// Generate "View Only" message for restricted fields
export const getRestrictedFieldMessage = (userRoles, fieldCategory) => {
  const currentRoles = userRoles.join(', ').toUpperCase();
  const requiredPermissions = [];
  
  // Find which roles have write access to this field
  Object.entries(PERMISSION_MATRIX).forEach(([role, permissions]) => {
    if (permissions[fieldCategory] === 'write') {
      requiredPermissions.push(role.toUpperCase());
    }
  });
  
  if (requiredPermissions.length > 0) {
    return `View Only - ${fieldCategory.replace('_', ' ').toUpperCase()} editing requires ${requiredPermissions.join(' or ')} role`;
  }
  
  return 'View Only - Insufficient permissions';
};

// Demo role switching (for testing)
export const switchUserRole = (newRole) => {
  // In production, this would update user context/session
  // For demo, we'll store in localStorage
  localStorage.setItem('demo_user_role', newRole);
  window.location.reload(); // Force refresh to apply new role
};

export const getDemoUserRole = () => {
  return localStorage.getItem('demo_user_role') || ROLES.FINANCE;
};

// Action-based permissions for row actions
export const canPerformAction = (userRoles, actionType) => {
  if (!userRoles || userRoles.length === 0) {
    return false;
  }

  const actionPermissions = {
    'view_product': [ROLES.OWNER, ROLES.ADMIN, ROLES.FINANCE, ROLES.OPS, ROLES.WAREHOUSE, ROLES.SALES, ROLES.ANALYST, ROLES.VIEWER],
    'edit_product': [ROLES.OWNER, ROLES.ADMIN, ROLES.FINANCE, ROLES.OPS],
    'archive_product': [ROLES.OWNER, ROLES.ADMIN, ROLES.OPS],
    'delete_product': [ROLES.OWNER, ROLES.ADMIN]
  };

  const allowedRoles = actionPermissions[actionType] || [];
  return userRoles.some(role => allowedRoles.includes(role));
};